import { AdminShell, isAdminTab, type AdminTab } from "@admin/AdminShell";
import { CalendarShell } from "@calendar/CalendarShell";
import { CompanionShell } from "@components/CompanionShell";
import { WriteErrorNotice } from "@components/WriteErrorNotice";
// Datadog's wrapper around TanStack Router's own `createRouter`: it subscribes
// to route resolution and names each RUM view after the matched route pattern
// rather than the raw URL (WEB-3316). Everything else below still comes from
// `@tanstack/react-router`.
import { createRouter } from "@datadog/browser-rum-react/tanstack-router";
import { TranscriptionProvider } from "@providers/TranscriptionProvider";
import { ReplayShell } from "@replay/ReplayShell";
import {
  createRootRoute,
  createRoute,
  Navigate,
  Outlet,
  RouterProvider,
} from "@tanstack/react-router";
import { toKnownTaskKind } from "@tasks/tasksClient";
import { TasksRoute } from "@tasks/TasksRoute";
import { AttentionShell } from "@views/AttentionShell";
import { validateWorkspaceSearch } from "@workspace/selection";
import { lazy, Suspense } from "react";

// Must stay a literal import.meta.env condition around a dynamic import: that's what
// lets Rollup skip emitting the DevInjectPanel chunk in non-dev builds (WEB-3313).
// A static import would ship the panel in every bundle regardless of runtime gates.
const DevInjectPanel =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEV_TOOLS === "true"
    ? lazy(() =>
        import("@components/DevInjectPanel").then((m) => ({
          default: m.DevInjectPanel,
        })),
      )
    : null;

const RootLayout = () => (
  <TranscriptionProvider>
    <Outlet />
    <WriteErrorNotice />
    {DevInjectPanel && (
      <Suspense fallback={null}>
        <DevInjectPanel />
      </Suspense>
    )}
  </TranscriptionProvider>
);

const rootRoute = createRootRoute({ component: RootLayout });

const companionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: validateWorkspaceSearch,
  component: CompanionShell,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  validateSearch: (search: Record<string, unknown>): { tab?: AdminTab } => ({
    tab: isAdminTab(search.tab) ? search.tab : undefined,
  }),
  component: AdminShell,
});

const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/calendar",
  validateSearch: (
    search: Record<string, unknown>,
  ): { week?: string; days?: 1 | 3 | 7 } => ({
    week: typeof search.week === "string" ? search.week : undefined,
    days:
      search.days === 1 || search.days === 3 || search.days === 7
        ? search.days
        : undefined,
  }),
  component: CalendarShell,
});

const tasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks",
  validateSearch: (
    search: Record<string, unknown>,
  ): { kind?: string; urgent?: true } => ({
    kind: toKnownTaskKind(search.kind),
    urgent: search.urgent === true ? true : undefined,
  }),
  component: TasksRoute,
});

const replayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/replay",
  validateSearch: (search: Record<string, unknown>): { visit?: string } => ({
    visit: typeof search.visit === "string" ? search.visit : undefined,
  }),
  component: ReplayShell,
});

const attentionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/attention",
  validateSearch: (search: Record<string, unknown>): { visit?: string } => ({
    visit: typeof search.visit === "string" ? search.visit : undefined,
  }),
  component: AttentionShell,
});

const routeTree = rootRoute.addChildren([
  companionRoute,
  adminRoute,
  calendarRoute,
  tasksRoute,
  replayRoute,
  attentionRoute,
]);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => <Navigate to="/" />,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export const AppShell = () => <RouterProvider router={router} />;
